/**
 * Family Hook
 * 
 * Fetches family data including white-label branding.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';

export const useFamily = (familyId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  
  return useQuery({
    queryKey: ['family', familyId],
    queryFn: () => authenticatedFetch(`/api/families/${familyId}`),
    enabled: !!familyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get current user's family
 */
export const useCurrentFamily = (options = {}) => {
  const { getAccessTokenSilently } = useAuth0();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';
  
  return useQuery({
    queryKey: ['currentFamily'],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        audience: "https://api.eternavaults.com"
      });
      
      const response = await fetch(`${API_BASE_URL}/api/families/my`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // 404 is valid (e.g., for partners who don't have families)
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: options.enabled !== false, // Allow disabling the query (for partners)
    retry: false, // Don't retry - 404 is valid for partners
    throwOnError: false // Don't throw on errors
  });
};

